// The Service Record screen: your rank, the commendation ladder with progress, and the wardrobe
// (gun finish + title) you wear for the posse. Pad first (DualSense): ↑↓ row, ←→ item, ✕ done (on contracts ✕ signs),
// ○ back. Same self-contained overlay pattern as the gun rack (ui/loadout.ts), so the saloon can
// open it from its own prop later.
//
//   const h = openRecord(document.body, tracker, name, () => …);

import {
  COMMENDATIONS, CONTRACTS, NO_CONTRACT, Commendation, FINISHES, Finish, RANKS, STATS, STAT_LABEL, earnedCount, finishOpen, nextRank, rankOf,
  rewardLabel, wornFinish, wornTitle,
} from '../../sim/progression';
import { firstPad, psGlyph } from '../input/padnav';
import { finishSwatch } from './finishes';
import { Tracker, achievementTitles } from './ledger';

const CSS = `
.sr-wrap { position: fixed; inset: 0; z-index: 40; display: flex; align-items: center; justify-content: center;
  background: radial-gradient(ellipse at 50% 110%, rgba(250,199,125,0.18), transparent 60%), rgba(10,12,11,0.55);
  font-family: 'Space Grotesk', system-ui, sans-serif; color: #f0e6c8; }
.sr-panel { width: min(1180px, calc(100vw - 24px)); max-height: calc(100vh - 24px); overflow: auto; padding: 22px 26px 18px; box-sizing: border-box;
  background: rgba(14,18,16,0.74); border: 1px solid rgba(250,199,125,0.35); border-radius: 4px; backdrop-filter: blur(6px); box-shadow: 0 30px 80px rgba(0,0,0,0.45); }
.sr-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.sr-title { font-family: 'Space Grotesk', sans-serif; font-weight: 600; letter-spacing: 0.5px; color: #fac77d; font-size: 34px; margin: 0; line-height: 1; }
.sr-sub { color: rgba(240,230,200,0.7); letter-spacing: 3px; font-size: 12px; text-transform: uppercase; margin: 6px 0 0; }
.sr-rank { text-align: right; }
.sr-rank .nm { font-size: 24px; font-weight: 700; color: #ffe6b0; letter-spacing: 1px; }
.sr-rank .nx { font-size: 12px; letter-spacing: 2px; color: rgba(240,230,200,0.6); text-transform: uppercase; }
.sr-meter { height: 5px; background: rgba(240,230,200,0.12); margin-top: 6px; width: 260px; margin-left: auto; position: relative; }
.sr-meter b { position: absolute; inset: 0 auto 0 0; background: linear-gradient(90deg, #d99423, #ffe6b0); }
.sr-cols { display: grid; grid-template-columns: 1.15fr 1fr; gap: 22px; margin-top: 16px; }
@media (max-width: 820px) { .sr-cols { grid-template-columns: 1fr; } .sr-meter { margin-left: 0; } .sr-rank { text-align: left; } }
.sr-h { font-size: 12px; letter-spacing: 3px; color: rgba(240,230,200,0.55); text-transform: uppercase; margin: 0 0 8px; }
.sr-row.on > .sr-h { color: #ffe6b0; }
.sr-list { display: flex; flex-direction: column; gap: 3px; }
.sr-c { display: grid; grid-template-columns: 18px 1fr auto; gap: 2px 10px; align-items: center; padding: 5px 10px 4px; background: rgba(0,0,0,0.24);
  border: 1px solid rgba(240,230,200,0.08); border-radius: 2px; }
.sr-c .ic { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid rgba(240,230,200,0.35); box-sizing: border-box; }
.sr-c.got .ic { background: #fac77d; border-color: #fac77d; position: relative; }
.sr-c.got .ic::after { content: ''; position: absolute; left: 4.5px; top: 1.5px; width: 4px; height: 8px; border: solid #1b1712; border-width: 0 2px 2px 0; transform: rotate(45deg); }
.sr-c .nm { font-size: 16px; font-weight: 700; letter-spacing: .3px; white-space: nowrap; margin-right: 8px; }
.sr-c .ln { display: flex; align-items: baseline; flex-wrap: wrap; min-width: 0; }
.sr-c.got .nm { color: #ffe6b0; }
.sr-c .how { font-size: 13px; color: rgba(240,230,200,0.62); }
.sr-c .rw { font-size: 13px; color: #fac77d; text-align: right; white-space: nowrap; }
.sr-c:not(.got) .rw { color: rgba(250,199,125,0.55); }
.sr-c .bar { grid-column: 2 / 4; margin-bottom: 1px; height: 3px; background: rgba(240,230,200,0.1); position: relative; }
.sr-c .bar b { position: absolute; inset: 0 auto 0 0; background: rgba(250,199,125,0.7); }
.sr-items { display: flex; gap: 8px; flex-wrap: wrap; padding: 2px; }
.sr-it { padding: 8px 10px 7px; border: 1px solid rgba(240,230,200,0.15); background: rgba(0,0,0,0.28); border-radius: 2px; cursor: pointer; min-width: 92px; }
.sr-it .sw { display: flex; gap: 2px; margin-bottom: 6px; }
.sr-it .sw i { width: 18px; height: 12px; display: block; border-radius: 1px; }
.sr-it .lb { font-size: 14px; font-weight: 700; white-space: nowrap; }
.sr-it.lock { opacity: 0.42; }
.sr-it.lock .lb::before { content: '\\1F512\\FE0E  '; font-size: 11px; }
.sr-it.sel { border-color: rgba(250,199,125,0.75); background: rgba(250,199,125,0.1); }
.sr-it.sel .lb::after { content: ' \\2713'; color: #fac77d; }
.sr-row.on .sr-it.cur { outline: 3px solid #ffe6b0; outline-offset: 2px; background: rgba(250,199,125,0.22); box-shadow: 0 0 18px rgba(250,199,125,0.35); opacity: 1; }
.sr-detail { min-height: 40px; margin: 8px 0 14px; font-size: 15px; color: rgba(240,230,200,0.85); line-height: 1.3; }
.sr-detail b { color: #ffe6b0; }
.sr-k { display: grid; grid-template-columns: 1fr; gap: 6px; }
.sr-kc { padding: 8px 10px; border: 1px dashed rgba(240,230,200,0.18); border-radius: 2px; }
.sr-kc.got { border-style: solid; border-color: rgba(250,199,125,0.45); }
.sr-kc .nm { font-weight: 700; font-size: 16px; }
.sr-kc .ds { font-size: 13px; color: rgba(240,230,200,0.65); }
.sr-k .sr-it { min-width: 0; cursor: pointer; }
.sr-kc.lock { opacity: 0.5; }
.sr-kc.sel { border-style: solid; border-color: #fac77d; background: rgba(250,199,125,0.12); }
.sr-kc.sel .nm::after { content: ' \\2713 SIGNED'; color: #fac77d; font-size: 12px; letter-spacing: 2px; }
.sr-kc .st { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: rgba(250,199,125,0.75); margin-top: 3px; }
.sr-stats { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(250,199,125,0.2); font-size: 12px; color: rgba(240,230,200,0.6); letter-spacing: 1px; text-transform: uppercase; }
.sr-stats b { color: #f0e6c8; font-size: 15px; margin-right: 5px; letter-spacing: 0; }
.sr-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 14px; }
.sr-foot .hint { color: rgba(240,230,200,0.6); font-size: 13px; max-width: 560px; }
.sr-btn { font: 700 17px 'Space Grotesk'; letter-spacing: 3px; text-transform: uppercase; padding: 9px 22px; background: rgba(250,199,125,0.14); border: 1px solid #fac77d;
  color: #ffe6b0; border-radius: 2px; cursor: pointer; }
.sr-btn .psg { margin-right: 8px; width: 1.15em; height: 1.15em; vertical-align: -0.22em; }
`;

let cssDone = false;

export function howTo(c: Commendation) {
  const n = c.n;
  const s = (one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  switch (c.stat) {
    case 'orders': return `Fill ${s('work order', 'work orders')}`;
    case 'islands': return `Clear ${s('island', 'islands')}`;
    case 'bosses': return `Scrap ${s('boss', 'bosses')}`;
    case 'extracts': return n === 1 ? 'Extract once' : `Extract ${n} times`;
    case 'dets': return `Land ${s('detonation', 'detonations')}`;
    case 'revives': return `Revive ${s('teammate', 'teammates')}`;
    case 'kills': return `Scrap ${s('robot', 'robots')}`;
    case 'runs': return `Clock ${s('shift', 'shifts')}`;
  }
}

export interface RecordHandle { close(): void; refresh(): void; readonly open: boolean }

/** The posse's side of contracts (from the live game). Without it the contracts list is read-only. */
export interface PosseContracts {
  /** bit per contract anyone riding has earned */
  mask(): number;
  /** the signed contract, NO_CONTRACT for none */
  current(): number;
  /** signing only happens in the saloon between rides */
  canSign(): boolean;
  sign(k: number): void;
}

export function openRecord(host: HTMLElement, tracker: Tracker, cowboy: string, onClose?: () => void, posse?: PosseContracts): RecordHandle {
  if (!cssDone) { cssDone = true; const s = document.createElement('style'); s.textContent = CSS; document.head.appendChild(s); }
  const l = tracker.ledger;
  const held = earnedCount(l);
  const rank = rankOf(held), nx = nextRank(held);
  const titles = tracker.titles();
  let finish = wornFinish(l);
  let title = wornTitle(l, achievementTitles());
  const cursor = [FINISHES.findIndex((f) => f.id === finish), Math.max(0, titles.indexOf(title)), 0];
  const rows = posse ? 3 : 2;
  let row = 0, isOpen = true;

  const wrap = document.createElement('div');
  wrap.className = 'sr-wrap';
  const rankLo = rank.at, rankHi = nx ? nx.at : rank.at;
  const pct = nx ? (held - rankLo) / Math.max(1, rankHi - rankLo) : 1;
  wrap.innerHTML = `
    <div class="sr-panel" role="dialog" aria-label="Service Record">
      <div class="sr-head">
        <div><h2 class="sr-title">Service Record</h2><div class="sr-sub"><span class="who"></span> · ${held} of ${COMMENDATIONS.length} commendations filed</div></div>
        <div class="sr-rank"><div class="nm">${rank.name}</div><div class="nx">${nx ? `${nx.at - held} more to ${nx.name}` : 'Top of the org chart'}</div>
          <div class="sr-meter"><b style="width:${Math.round(pct * 100)}%"></b></div></div>
      </div>
      <div class="sr-cols">
        <div><div class="sr-h">Commendations · posse ones count for everyone riding</div><div class="sr-list"></div></div>
        <div>
          <div class="sr-row" data-r="0"><div class="sr-h">Gun finish · the posse sees it</div><div class="sr-items fin"></div></div>
          <div class="sr-row" data-r="1" style="margin-top:12px"><div class="sr-h">Title</div><div class="sr-items ttl"></div></div>
          <div class="sr-detail"></div>
          <div class="sr-row" data-r="2"><div class="sr-h">Contracts · opt-in, any one in the posse opens it for all</div>
          <div class="sr-k"></div></div>
        </div>
      </div>
      <div class="sr-stats"></div>
      <div class="sr-foot">
        <span class="hint">Every gun, grenade and class is open from day one. The record earns how you look and how hard you want it.</span>
        <button class="sr-btn" data-act="done">${psGlyph('cross')}Done</button>
      </div>
    </div>`;
  (wrap.querySelector('.who') as HTMLElement).textContent = cowboy;

  const list = wrap.querySelector('.sr-list') as HTMLElement;
  for (const c of COMMENDATIONS) {
    const got = !!l.earned[c.id];
    const have = Math.min(c.n, l.stats[c.stat]);
    const d = document.createElement('div');
    d.className = 'sr-c' + (got ? ' got' : '');
    d.innerHTML = `<span class="ic"></span><div class="ln"><span class="nm">${c.name}</span><span class="how">${howTo(c)} · ${c.scope === 'posse' ? 'posse' : 'you'}${got ? '' : ` · ${have}/${c.n}`}</span></div><div class="rw">${rewardLabel(c.reward).replace('Gun finish: ', '').replace('Title: ', 'Title · ').replace('Contract: ', 'Contract · ')}</div>${got ? '' : `<div class="bar"><b style="width:${Math.round((have / c.n) * 100)}%"></b></div>`}`;
    list.appendChild(d);
  }

  const finEl = wrap.querySelector('.fin') as HTMLElement;
  FINISHES.forEach((f, i) => {
    const it = document.createElement('div');
    it.className = 'sr-it' + (finishOpen(l, f.id) ? '' : ' lock');
    it.innerHTML = `<div class="sw">${finishSwatch(f.id).map((c) => `<i style="background:${c}"></i>`).join('')}</div><div class="lb">${f.name}</div>`;
    it.onmouseenter = () => { row = 0; cursor[0] = i; render(); };
    it.onclick = () => { row = 0; cursor[0] = i; choose(); render(); };
    finEl.appendChild(it);
  });
  const ttlEl = wrap.querySelector('.ttl') as HTMLElement;
  titles.forEach((t, i) => {
    const it = document.createElement('div');
    it.className = 'sr-it';
    const lb = document.createElement('div'); lb.className = 'lb'; lb.textContent = t;
    it.appendChild(lb);
    it.onmouseenter = () => { row = 1; cursor[1] = i; render(); };
    it.onclick = () => { row = 1; cursor[1] = i; choose(); render(); };
    ttlEl.appendChild(it);
  });
  const kEl = wrap.querySelector('.sr-k') as HTMLElement;
  const kSrc = (id: number) => COMMENDATIONS.find((c) => c.reward.kind === 'contract' && c.reward.id === id)!;
  CONTRACTS.forEach((k, i) => {
    const d = document.createElement('div');
    d.className = posse ? 'sr-it sr-kc' : 'sr-kc';
    d.innerHTML = `<div class="nm">${k.name}</div><div class="ds">${k.blurb}</div><div class="st"></div>`;
    if (posse) {
      d.onmouseenter = () => { row = 2; cursor[2] = i; render(); };
      d.onclick = () => { row = 2; cursor[2] = i; choose(); render(); };
    }
    kEl.appendChild(d);
  });
  /** a contract the posse can sign: you earned it, or someone riding did */
  const kOpen = (id: number) => (posse ? (posse.mask() >> id) & 1 : 0) === 1 || !!l.earned[kSrc(id).id];
  function renderContracts() {
    const cur = posse ? posse.current() : NO_CONTRACT;
    kEl.querySelectorAll('.sr-kc').forEach((d, i) => {
      const k = CONTRACTS[i], src = kSrc(k.id);
      const mine = !!l.earned[src.id], open = kOpen(k.id);
      d.classList.toggle('got', open);
      d.classList.toggle('lock', !open);
      d.classList.toggle('sel', cur === k.id);
      let st: string;
      if (!open) st = `Locked · ${src.name}: ${howTo(src).toLowerCase()}`;
      else if (!posse) st = 'Earned · sign it with Pop at the saloon';
      else if (cur === k.id) st = posse.canSign() ? 'Signed for the next ride · choose again to tear it up' : 'Signed for this ride';
      else if (!posse.canSign()) st = 'Sign at the saloon between rides';
      else st = mine ? 'Earned · choose to sign for the next ride' : 'A posse mate earned it · choose to sign';
      (d.querySelector('.st') as HTMLElement).textContent = st;
    });
  }
  (wrap.querySelector('.sr-stats') as HTMLElement).innerHTML = STATS.map((s) => `<span><b>${l.stats[s]}</b>${STAT_LABEL[s][1]}</span>`).join('');

  const detail = wrap.querySelector('.sr-detail') as HTMLElement;
  function source(f: Finish) { return COMMENDATIONS.find((c) => c.reward.kind === 'finish' && c.reward.id === f); }

  function choose() {
    if (row === 2) {
      const k = CONTRACTS[cursor[2]];
      if (!posse || !posse.canSign() || !kOpen(k.id)) return;
      posse.sign(posse.current() === k.id ? NO_CONTRACT : k.id);
      return;
    }
    if (row === 0) {
      const f = FINISHES[cursor[0]];
      if (!finishOpen(l, f.id)) return;
      finish = f.id;
    } else title = titles[cursor[1]];
    tracker.setLook(finish, title);
  }

  function render() {
    wrap.querySelectorAll('.sr-row').forEach((re, ri) => {
      re.classList.toggle('on', ri === row);
      re.querySelectorAll('.sr-it').forEach((it, ii) => {
        it.classList.toggle('cur', ii === cursor[ri]);
        if (ri < 2) it.classList.toggle('sel', ri === 0 ? FINISHES[ii].id === finish : titles[ii] === title);
      });
    });
    renderContracts();
    if (row === 2) {
      const k = CONTRACTS[cursor[2]];
      detail.innerHTML = '';
      const b = document.createElement('b'); b.textContent = k.name + '.';
      detail.append(b, document.createTextNode(` ${k.blurb} One contract per ride, for the whole posse. Lasts until someone tears it up.`));
    } else if (row === 0) {
      const f = FINISHES[cursor[0]];
      const src = source(f.id);
      detail.innerHTML = `<b>${f.name}.</b> ${f.blurb}${finishOpen(l, f.id) ? '' : ` <br>Locked: earn <b>${src?.name}</b> (${src ? howTo(src).toLowerCase() : ''}).`}`;
    } else {
      const t = titles[cursor[1]];
      const isRank = RANKS.some((r) => r.name === t);
      detail.innerHTML = '';
      const b = document.createElement('b'); b.textContent = t;
      detail.append(b, document.createTextNode(isRank ? ' · a rank you have reached.' : ' · earned on the job.'));
    }
  }

  function move(dr: number, dc: number) {
    if (dr) row = (row + dr + rows) % rows;
    if (dc) {
      const n = row === 0 ? FINISHES.length : row === 1 ? titles.length : CONTRACTS.length;
      cursor[row] = (cursor[row] + dc + n) % n;
      // finishes and titles try on as you move; a contract needs a deliberate ✕
      if (row < 2) choose();
    }
    render();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
    onClose?.();
  }
  (wrap.querySelector('[data-act=done]') as HTMLElement).onclick = close;

  const onKey = (e: KeyboardEvent) => {
    const k = e.code;
    if (k === 'ArrowUp' || k === 'KeyW') move(-1, 0);
    else if (k === 'ArrowDown' || k === 'KeyS') move(1, 0);
    else if (k === 'ArrowLeft' || k === 'KeyA') move(0, -1);
    else if (k === 'ArrowRight' || k === 'KeyD') move(0, 1);
    else if ((k === 'Enter' || k === 'Space') && row === 2) { choose(); render(); }
    else if (k === 'Enter' || k === 'Space' || k === 'Escape' || k === 'Backspace') close();
    else return;
    e.preventDefault(); e.stopPropagation();
  };
  window.addEventListener('keydown', onKey, true);

  // pad: own poll; buttons held when the screen opened are ignored until released
  const heldBtn = new Set<number>();
  let first = true, raf = 0, rep = 0, repDir = '';
  const poll = () => {
    if (!isOpen) return;
    const gp = firstPad();
    if (gp) {
      const now = performance.now();
      const down = (i: number) => !!gp.buttons[i]?.pressed;
      const ax = gp.axes[0] ?? 0, ay = gp.axes[1] ?? 0;
      const dir = down(12) || ay < -0.6 ? 'u' : down(13) || ay > 0.6 ? 'd' : down(14) || ax < -0.6 ? 'l' : down(15) || ax > 0.6 ? 'r' : '';
      if (first) { for (let i = 0; i < 16; i++) if (down(i)) heldBtn.add(i); first = false; if (dir) { repDir = dir; rep = now + 1e9; } }
      for (const i of [...heldBtn]) if (!down(i)) heldBtn.delete(i);
      const press = (i: number) => down(i) && !heldBtn.has(i) && (heldBtn.add(i), true);
      if (dir !== repDir) { repDir = dir; rep = now + 320; if (dir) step(dir); }
      else if (dir && now > rep) { rep = now + 110; step(dir); }
      if (press(0)) { if (row === 2) { choose(); render(); } else close(); }
      if (press(1)) close();
      // right stick scrolls the panel on small screens
      const ry = gp.axes[3] ?? 0;
      if (Math.abs(ry) > 0.25) (wrap.firstElementChild as HTMLElement).scrollTop += ry * 14;
    }
    raf = requestAnimationFrame(poll);
  };
  const step = (d: string) => move(d === 'u' ? -1 : d === 'd' ? 1 : 0, d === 'l' ? -1 : d === 'r' ? 1 : 0);
  raf = requestAnimationFrame(poll);

  host.appendChild(wrap);
  render();
  return { close, refresh: render, get open() { return isOpen; } };
}
